import Link from "next/link";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";

import useDisableBodyScroll from "@/hooks/useDisableBodyScroll";
import useMediaQuery from "@/hooks/useMediaQuery";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import useScrollDirection from "@/hooks/useScrollDirection";
import { useShoppingCart } from "@/context/cart";
import { useUser } from "@/context/user";
import { useSiteSettings } from "@/context/siteSettings";

import User from "/packages/icons/user.svg";
import Heart from "/packages/icons/heart.svg";
import Search from "/packages/icons/search-light.svg";
import Hamburger from "/packages/icons/hamburger.svg";
import Bag from "/packages/icons/shopping-bag.svg";

import styles from "./Nav.module.scss";

import MobileMenu from "./MobileMenu";
import SecondaryNav from "./SecondaryNav";
import dynamic from "next/dynamic";
const Searchbar = dynamic(() => import("./Searchbar"), { ssr: false });
const Location = dynamic(() => import("@/components/location/Location"), { ssr: false });
import Button from "@/components/Button";
import Cart from "@/components/bag/index.js";

export default function Nav({ pathname }) {
  const { logo, dispensaryName, menu } = useSiteSettings() || {};

  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);

  const isBreakPointTablet = useMediaQuery(768);
  const { favoriteQuantity } = useUser();
  const { cartModal } = useShoppingCart();

  const scrollUp = useScrollDirection(100);

  const anyModalOpen = isMobileMenuOpen || isSearchOpen || cartModal;

  return (
    <>
      <div className={styles.wrapper}>
        <div className={`${styles.navbar} ${(scrollUp || anyModalOpen) ? styles.show : styles.hide}`}>
          <nav className="border-bottom relative">
            <div className={`${styles.nav} container container__slim`}>

              {menu?.mainMenu && (
                <ModalMenu
                  title="menu"
                  icon={<Hamburger />}
                  onToggle={setMobileMenuOpen}
                  className={styles.modalMobileMenu}
                >
                  <MobileMenu menuItems={menu.mainMenu} />
                </ModalMenu>
              )}

              <Link href="/" aria-label="home" className={`${styles.logo} pr-100`}>
                {logo?.asset?.url && (
                  <img src={logo.asset.url} alt={`${dispensaryName || 'Logo'}`} />
                )}
              </Link>

              {!isBreakPointTablet && (
                <>
                  <Searchbar />
                  <Location />
                </>
              )}

              <div style={{ display: 'flex' }}>
                {isBreakPointTablet && (
                  <ModalMenu
                    title="search"
                    icon={<Search />}
                    onToggle={setSearchOpen}
                    className={styles.modalSearchMenu}
                  >
                    <Searchbar />
                  </ModalMenu>
                )}

                <Button type="link" className="icon" href="/login" ariaLabel="account login">
                  <User />
                </Button>

                <Button type="link" className="icon" href="/favorites" ariaLabel="favorite products">
                  <Heart />
                  {favoriteQuantity > 0 && (
                    <span className={styles.bagQuantity}>{favoriteQuantity}</span>
                  )}
                </Button>

                <ModalMenu
                  title="shopping-bag"
                  icon={<Bag />}
                  className={styles.janeCart}
                  id="cartModal"
                >
                  <Cart />
                </ModalMenu>
              </div>
            </div>
          </nav>

          {isBreakPointTablet && <Location />}
        </div>
      </div>

      <SecondaryNav pathname={pathname} />
    </>
  );
}

function ModalMenu({ title, icon, children, onToggle, className = "", id }) {
  const router = useRouter();
  const { cartQuantity, cartModal, setCartModal } = useShoppingCart();

  const isShoppingBag = title === "shopping-bag";
  const [localOpen, setLocalOpen] = useState(false);

  const open = isShoppingBag ? cartModal : localOpen;
  const setOpen = isShoppingBag ? setCartModal : setLocalOpen;

  useDisableBodyScroll(open);

  const handleClick = () => setOpen(!open);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (onToggle) onToggle(open);
  }, [open]);

  useEffect(() => {
    if (router.pathname === '/checkout' && isShoppingBag) {
      setOpen(true);
    }
  }, [router.pathname]);

  const modalMenuRef = useRef(null);
  useOnClickOutside(modalMenuRef, () => handleClose(), "#openModal");

  const renderChildren = () => {
    // if (!open) return null;
    return React.Children.map(children, (child) =>
      React.cloneElement(child, {
        open,
        handleClick,
        handleClose,
      })
    );
  };

  return (
    <div className={`${styles.modalMenu} ${className}`} ref={modalMenuRef}>
      <Button type="button" className="icon" onClick={handleClick} ariaLabel={title}>
        {icon}
        {title === "shopping-bag" && cartQuantity > 0 && (
          <span className={styles.bagQuantity}>{cartQuantity}</span>
        )}
      </Button>

      <div
        className={`${styles.modalMenu__modal} ${open ? styles.modalMenu__modal__active : ""}`}
        id={id}
        inert={!open ? '' : undefined}
      >
        <div className="relative">
          <div className={styles.modalMenu__content}>
              {renderChildren()}
          </div>
        </div>
      </div>

      <div
        className={`${styles.backdrop} ${open ? styles.backdrop_active : ""}`}
        onClick={handleClose}
      />
    </div>
  );
}